import React from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, PanelHeader, PanelContent } from '@deque/cauldron-react';
import { Link } from 'react-router-dom';
import {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import styles from './TestDetails.css';
import type {
  FilteredUserFlowResults,
  Filters
} from '@deque/axe-devtools-issues-utils';

interface UserFlowOverviewProps {
  results: FilteredUserFlowResults;
  filters: Filters;
}

interface IssueLinkProps extends React.HTMLAttributes<HTMLElement> {
  children: number;
  to: string;
}

const IssueLink = ({ children: count, ...props }: IssueLinkProps) => {
  return count > 0 ? <Link {...props}>{count}</Link> : <>{count}</>;
};

const UserFlowOverview: React.FC<UserFlowOverviewProps> = ({
  results,
  filters
}) => {
  const { t } = useTranslation();
  const issuesLink = (newFilters?: Partial<Filters>) => {
    const search = getSearchParamsFromFilters({
      ...getBaseFilters(filters),
      ...newFilters
    });
    return `${location.pathname}/issues?${search}`;
  };

  return (
    <section className={styles.overview} aria-labelledby="userflow-overview">
      <h2 id="userflow-overview">{t('Test Record Overview')}</h2>
      <Panel>
        <PanelHeader>
          <h3 id="userflow-total-issues">{t('Total Unique Issues')}</h3>
        </PanelHeader>
        <PanelContent>
          {results.overview.currUniqueIssues === 0 ? (
            <div className={styles.overview__total}>0</div>
          ) : (
            <Link
              className={styles.overview__total}
              to={issuesLink()}
              id="userflow-total-issues-link"
              aria-labelledby="userflow-total-issues-link userflow-total-issues"
            >
              {results.overview.currUniqueIssues}
            </Link>
          )}
        </PanelContent>
      </Panel>
      <Panel>
        <PanelHeader>
          <h3>{t('Total Issues By Impact')}</h3>
        </PanelHeader>
        <PanelContent>
          <dl>
            <dt id="severity-critical">{t('Critical')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['critical'] })}
                id="severity-critical-link"
                aria-labelledby="severity-critical-link severity-critical"
              >
                {results.overview.numIssueSeverity.critical.totalCurrent}
              </IssueLink>
            </dd>
            <dt id="severity-serious">{t('Serious')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['serious'] })}
                id="severity-serious-link"
                aria-labelledby="severity-serious-link severity-serious"
              >
                {results.overview.numIssueSeverity.serious.totalCurrent}
              </IssueLink>
            </dd>
            <dt id="severity-moderate">{t('Moderate')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['moderate'] })}
                id="severity-moderate-link"
                aria-labelledby="severity-moderate-link severity-moderate"
              >
                {results.overview.numIssueSeverity.moderate.totalCurrent}
              </IssueLink>
            </dd>
            <dt id="severity-minor">{t('Minor')}</dt>
            <dd>
              <IssueLink
                to={issuesLink({ impact: ['minor'] })}
                id="severity-minor-link"
                aria-labelledby="severity-minor-link severity-minor"
              >
                {results.overview.numIssueSeverity.minor.totalCurrent}
              </IssueLink>
            </dd>
          </dl>
        </PanelContent>
      </Panel>
    </section>
  );
};

export default UserFlowOverview;
